import { useCallback } from 'react'
import type { Translation } from '../../../translations'

interface SearchHeaderProps {
    query: string
    onQueryChange: (value: string) => void
    onSubmit: () => void
    placeholder: string
    suggestions: string[]
    isSuggestOpen: boolean
    suggestIndex: number
    onSuggestVisibilityChange: (open: boolean) => void
    onSuggestIndexChange: (index: number) => void
    onSuggestionSelect: (value: string) => void
    loading: boolean
    hasNextPage: boolean
    onSaveSearch: () => void
    t: Translation
}

export function SearchHeader({
    query,
    onQueryChange,
    onSubmit,
    placeholder,
    suggestions,
    isSuggestOpen,
    suggestIndex,
    onSuggestVisibilityChange,
    onSuggestIndexChange,
    onSuggestionSelect,
    loading,
    hasNextPage,
    onSaveSearch,
    t
}: SearchHeaderProps) {
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        const hasSuggestions = isSuggestOpen && suggestions.length > 0
        if (hasSuggestions && e.key === 'ArrowDown') {
            e.preventDefault()
            onSuggestIndexChange(Math.min(suggestIndex + 1, suggestions.length - 1))
            return
        }
        if (hasSuggestions && e.key === 'ArrowUp') {
            e.preventDefault()
            onSuggestIndexChange(Math.max(suggestIndex - 1, -1))
            return
        }
        if (hasSuggestions && e.key === 'Escape') {
            e.preventDefault()
            onSuggestVisibilityChange(false)
            onSuggestIndexChange(-1)
            return
        }
        if (e.key === 'Enter') {
            if (hasSuggestions && suggestIndex >= 0) {
                e.preventDefault()
                onSuggestionSelect(suggestions[suggestIndex])
                return
            }
            onSubmit()
        }
    }, [isSuggestOpen, suggestions, suggestIndex, onSuggestIndexChange, onSuggestVisibilityChange, onSuggestionSelect, onSubmit])

    return (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
            <div className="relative flex-1">
                <input
                    type="text"
                    value={query}
                    onChange={(e) => {
                        onQueryChange(e.target.value)
                        onSuggestVisibilityChange(true)
                        onSuggestIndexChange(-1)
                    }}
                    onFocus={() => {
                        if (suggestions.length > 0) onSuggestVisibilityChange(true)
                    }}
                    onBlur={() => {
                        window.setTimeout(() => onSuggestVisibilityChange(false), 120)
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                />

                {isSuggestOpen && suggestions.length > 0 && (
                    <div className="absolute top-full z-30 mt-2 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                        {suggestions.map((s, idx) => (
                            <button
                                key={s}
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => onSuggestionSelect(s)}
                                className={`w-full px-4 py-2 text-left text-sm ${idx === suggestIndex ? 'bg-slate-50' : 'bg-white'} hover:bg-slate-50`}
                                title={s}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={onSubmit}
                    disabled={loading && !hasNextPage}
                    className="flex-1 rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50 inline-flex items-center justify-center"
                    aria-label={t.searchButton}
                    title={t.searchButton}
                >
                    {loading && !hasNextPage ? (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" className="h-5 w-5 animate-spin" aria-hidden="true">
                            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
                            <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                            <path d="M10 2a8 8 0 1 0 4.9 14.32l4.39 4.39 1.42-1.42-4.39-4.39A8 8 0 0 0 10 2zm0 2a6 6 0 1 1 0 12 6 6 0 0 1 0-12z" />
                        </svg>
                    )}
                </button>
                <button
                    type="button"
                    onClick={onSaveSearch}
                    className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-700 shadow-sm hover:bg-slate-50"
                    title={t.searchSave}
                    aria-label={t.searchSave}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                        <path d="M17 3H7a2 2 0 0 0-2 2v16l7-3 7 3V5a2 2 0 0 0-2-2z" />
                    </svg>
                </button>
            </div>
        </div>
    )
}
